import { ExternalLink } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A link that leaves the app — a carrier's tracking page, a HubSpot deal, a
 * QuickBooks report.
 *
 * Always carries the outbound arrow, always opens in a new tab with
 * `rel="noreferrer"`. The arrow is the point: it sets the expectation that the
 * click leaves CinchOps, which matters most in a table where every other link
 * navigates in place.
 *
 * Renders as plain muted text (no arrow, not focusable) when `href` is null,
 * so a row with nothing to link to doesn't need a conditional at the call site.
 */
export function LinkOut({
  href,
  children,
  size = "sm",
  className,
}: {
  href: string | null | undefined;
  children: React.ReactNode;
  /** `xs` for inside dense table cells, `sm` elsewhere. */
  size?: "xs" | "sm";
  className?: string;
}) {
  const text = size === "xs" ? "text-xs" : "text-sm";
  if (!href) {
    return (
      <span className={cn(text, "text-muted-foreground", className)}>{children}</span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={cn(
        "inline-flex items-center gap-1 font-medium text-primary hover:underline",
        "focus-visible:outline-none focus-visible:underline",
        text,
        className,
      )}
    >
      {children}
      <ExternalLink className={size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5"} aria-hidden />
    </a>
  );
}
